import { useMemo, useState } from "react";

import { SelectProgrammingState } from "@pages/ClinicalReportDetails/SelectProgramming/useSelectProgramming";

export type SortOption = "recently_updated" | "last_worked_on" | "alphabetical" | "status";

/** Match dropdown ids (enum values like NOT_ACTIVE or overrides like "On Hold") to API goal.status strings. */
function normalizeStatusKey(s: string): string {
  return s.trim().replace(/\s+/g, "_").toUpperCase();
}

export function useSelectProgrammingFilters(state: SelectProgrammingState) {
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState<string[]>([]);
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("recently_updated");
  const [activeTab, setActiveTab] = useState<"all" | "selected">("all");

  // Filter & sort logic
  const filteredGoals = useMemo(() => {
    let result = [...state];

    // Search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (g) => g.title.toLowerCase().includes(lower) || g.targets?.some((t) => t?.title.toLowerCase().includes(lower)),
      );
    }

    // Date range filter
    if (dateRangeFilter.length > 0) {
      const presetDays = dateRangeFilter.filter((v) => v !== "custom").map(Number);
      if (presetDays.length > 0) {
        const maxDays = Math.max(...presetDays);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - maxDays);
        result = result.filter((g) => new Date(g.lastUpdatedDate) >= cutoffDate);
      }
      if (dateRangeFilter.includes("custom") && customDateRange.start && customDateRange.end) {
        const start = new Date(customDateRange.start);
        const end = new Date(customDateRange.end);
        end.setHours(23, 59, 59, 999);
        result = result.filter((g) => {
          const d = new Date(g.lastUpdatedDate);
          return d >= start && d <= end;
        });
      }
    }

    // Status filter
    if (statusFilter.length > 0) {
      const filterSet = new Set(statusFilter.map(normalizeStatusKey));
      result = result.filter((g) => {
        const goalStatus = normalizeStatusKey(g.status ?? "");
        return filterSet.has(goalStatus);
      });
    }

    // Tab filter
    if (activeTab === "selected") {
      result = result.filter((g) => g.checked);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "alphabetical":
          return a.title.localeCompare(b.title);
        case "status":
          return (a.status ?? "").localeCompare(b.status ?? "");
        case "last_worked_on":
        case "recently_updated":
        default:
          return new Date(b.lastUpdatedDate).getTime() - new Date(a.lastUpdatedDate).getTime();
      }
    });

    return result;
  }, [state, searchTerm, dateRangeFilter, customDateRange, statusFilter, sortBy, activeTab]);

  return {
    // Filter state
    searchTerm,
    setSearchTerm,
    dateRangeFilter,
    setDateRangeFilter,
    customDateRange,
    setCustomDateRange,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    activeTab,
    setActiveTab,

    // Goal list
    filteredGoals,
  };
}
